import { fetchToken, MetaLayerDefinition, SearchResultFlat } from '@agissc/ktag-map';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import config from '../../utils/AppConfig';

export interface UserFavorite {
  id: string;
  value: SearchResultFlat | MetaLayerDefinition;
  type: string;
  createdAt: Date;
  createdBy: string;
}

const fetchUserFavorites = async () => {
  const headers = new Headers();
  const token = await fetchToken();
  headers.set('Authorization', `Bearer ${token}`);

  const url = `${config.apis.userFavoritesLocation}/self`;
  const requestApi = new Request(url);
  const response = await fetch(requestApi, {
    method: 'GET',
    headers,
  });

  const result = (await response.json()) as UserFavorite[];
  return result;
};

const addUserFavorite = async (userFavorite: UserFavorite) => {
  const token = await fetchToken();
  const url = `${config.apis.userFavoritesLocation}/self`;

  const requestApi = new Request(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userFavorite),
  });
  const response = await fetch(requestApi);
  return (await response.json()) as UserFavorite;
};

const removeUserFavorite = async (id: string) => {
  const token = await fetchToken();

  const url = `${config.apis.userFavoritesLocation}/${id}`;
  const requestApi = new Request(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const response = await fetch(requestApi);
  return response;
};

export const useUserFavorite = () => {
  return useQuery<UserFavorite[]>({
    queryKey: ['userFavorites'],
    queryFn: () => fetchUserFavorites(),
    gcTime: 3600000,
    staleTime: Infinity,
  });
};

export const useAddUserFavorite = () => {
  const queryClient = useQueryClient();

  return useMutation<UserFavorite, Error, UserFavorite>({
    mutationFn: addUserFavorite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userFavorites'] });
    },
  });
};

export const useRemoveUserFavorite = () => {
  const queryClient = useQueryClient();

  return useMutation<Response, Error, string>({
    mutationFn: removeUserFavorite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userFavorites'] });
    },
  });
};
